import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IMappingSpecie from '../../interfaces/IMappingSpecie'
import { SearchRequest, MappingSpecieSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { mappingSpecieList, mappingSpecieSave } from '../../services/api'

interface MappingSpecieContextState extends BaseContextState {
  mappingSpecies: Array<IMappingSpecie> | null,
  mappingSpecie: IMappingSpecie | null
}

interface MappingSpecieContextData extends MappingSpecieContextState {
  resetErrors: Function,
  requestMappingSpecieList: (args: SearchRequest) => Promise<any>,
  requestMappingSpecieFind: (args: SearchRequest) => Promise<any>,
  requestMappingSpecieSave: (args: MappingSpecieSaveRequest) => Promise<void>,
}

// Create context
const { Provider, Consumer } = createContext({} as MappingSpecieContextData)

// Export consumer
export const MappingSpecieConsumer = Consumer

export class MappingSpecieProvider extends BaseContextProvider {
  state: MappingSpecieContextState = {
    mappingSpecies: null,
    mappingSpecie: null
  }

  /**
   * Make api request for mapping specie list
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingSpecieList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await mappingSpecieList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'mappingSpecies'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping specie finder
   * 
   * @param {SearchRequest} payload 
   */
  requestMappingSpecieFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        mappingSpecie: null
      }, async () => {
        payload.search = {
          ...payload.search,
          shelter: {
            with: {
              _fields: ["id", "uuid", "name"]
            }
          },
          specie: {
            with: true
          }
        }
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await mappingSpecieList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'mappingSpecie'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for mapping specie save
   * 
   * @param {MappingSpecieSaveRequest} payload 
   */
  requestMappingSpecieSave = (payload: MappingSpecieSaveRequest) => {
    // No need to null `mappingSpecie` on save: the form stays mounted (its render
    // no longer depends on this value) and re-fetches fresh whenever the modal reopens.
    return new Promise<void>((resolve, reject) => this.setState({
      fetching: true,
      error: null,
      // mappingSpecie: null,
    }, async () => {
      try {
        const response = await mappingSpecieSave(payload)

        // Process our response
        const result = await this.processResponse(response, {
          mappings: [
            { origin: 'mapping_specie', destination: 'mappingSpecie' }
          ]
        })

        // throw if error happend
        if (result.error) {
          return reject(new Error(result.error))
        }
      } finally {
        return resolve()
      }
    }))
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestMappingSpecieList: this.requestMappingSpecieList,
          requestMappingSpecieFind: this.requestMappingSpecieFind,
          requestMappingSpecieSave: this.requestMappingSpecieSave
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
